import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Paper, TextField, Button, Grid,
    FormControlLabel, Checkbox, Alert, Snackbar, CircularProgress,
    Card, CardContent, FormLabel, FormGroup, Stack
} from '@mui/material';
import { Warning as WarningIcon, Send as SendIcon } from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    IncidentReportClient,
    saveIncidentReport
} from '../services/incidentReport';
import { sendIncidentReportNotification } from '../services/email';

const defaultIncidentReport: IncidentReportClient = {
    incidentDateTime: '',
    location: '',
    travellersInvolved: '',
    incidentTypeMedical: false,
    incidentTypeSecurity: false,
    incidentTypeAccident: false,
    incidentTypeNearMiss: false,
    incidentTypeOther: false,
    incidentTypeOtherDetails: '',
    description: '',
    immediateActions: '',
    notifiedDutyOfficer: false,
    notifiedSecurityFocalPoint: false,
    notifiedMd: false,
    notifiedClient: false,
    notifiedAigInsurer: false,
    followupActions: '',
    completedBy: '',
    completedDate: '',
    status: 'draft',
    travelFormId: null,
};

const IncidentReportPage: React.FC = () => {
    const navigate = useNavigate();
    const [report, setReport] = useState<IncidentReportClient>(defaultIncidentReport);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [user, setUser] = useState<any>(null);

    // Load user info and reset form
    useEffect(() => {
        const loadUser = async () => {
            const principal = await getClientPrincipal();
            setUser(principal);
            // Always start with a fresh form
            setReport({
                ...defaultIncidentReport,
                completedBy: principal?.userDetails || principal?.userId || '',
                completedDate: new Date().toISOString().split('T')[0]
            });
        };
        loadUser();
    }, []);

    const handleSave = async (submit: boolean = false) => {
        if (!user) {
            setError('User not authenticated');
            return;
        }

        // Validation
        if (!report.incidentDateTime) {
            setError('Please enter the date and time of incident');
            return;
        }
        if (!report.location) {
            setError('Please enter the location');
            return;
        }
        if (!report.travellersInvolved) {
            setError('Please enter the travellers involved');
            return;
        }
        if (!report.description) {
            setError('Please enter a description of the incident');
            return;
        }
        if (!report.completedBy) {
            setError('Please enter who completed this form');
            return;
        }
        if (!report.completedDate) {
            setError('Please enter the completion date');
            return;
        }

        // Check at least one incident type is selected
        const hasIncidentType = report.incidentTypeMedical || 
                               report.incidentTypeSecurity || 
                               report.incidentTypeAccident || 
                               report.incidentTypeNearMiss || 
                               report.incidentTypeOther;
        if (!hasIncidentType) {
            setError('Please select at least one incident type');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const updatedReport: IncidentReportClient = {
                ...report,
                status: 'submitted'
            };

            const saved = await saveIncidentReport(
                updatedReport,
                user.userId,
                user.userDetails || user.userId,
                user.userDetails || user.userId
            );

            // Prepare email data
            const incidentTypes: string[] = [];
            if (report.incidentTypeMedical) incidentTypes.push('Medical');
            if (report.incidentTypeSecurity) incidentTypes.push('Security');
            if (report.incidentTypeAccident) incidentTypes.push('Accident');
            if (report.incidentTypeNearMiss) incidentTypes.push('Near Miss');
            if (report.incidentTypeOther) incidentTypes.push('Other');

            const partiesNotified: string[] = [];
            if (report.notifiedDutyOfficer) partiesNotified.push('Duty Officer');
            if (report.notifiedSecurityFocalPoint) partiesNotified.push('Security Focal Point');
            if (report.notifiedMd) partiesNotified.push('MD');
            if (report.notifiedClient) partiesNotified.push('Client');
            if (report.notifiedAigInsurer) partiesNotified.push('AIG/Insurer');

            // Send email notification
            await sendIncidentReportNotification({
                submitter_name: user.userDetails || user.userId,
                submitter_email: user.userId,
                incident_datetime: report.incidentDateTime,
                location: report.location,
                travellers_involved: report.travellersInvolved,
                incident_types: incidentTypes,
                incident_type_other_details: report.incidentTypeOtherDetails,
                description: report.description,
                immediate_actions: report.immediateActions,
                parties_notified: partiesNotified,
                followup_actions: report.followupActions,
                completed_by: report.completedBy,
                completed_date: report.completedDate
            });

            setSnackbarMessage('Incident report submitted successfully. Redirecting...');
            setSnackbarOpen(true);

            // Redirect to CV search page after 2 seconds
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to submit incident report');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof IncidentReportClient, value: any) => {
        setReport(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                    <Typography variant="h4" component="h1">
                        Incident Report Form
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Stack spacing={4}>
                    {/* Basic Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Incident Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Date/Time of Incident"
                                        type="datetime-local"
                                        value={report.incidentDateTime}
                                        onChange={(e) => handleInputChange('incidentDateTime', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Location"
                                        value={report.location}
                                        onChange={(e) => handleInputChange('location', e.target.value)}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Traveller(s) Involved"
                                        value={report.travellersInvolved}
                                        onChange={(e) => handleInputChange('travellersInvolved', e.target.value)}
                                        multiline
                                        rows={2}
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Incident Type */}
                    <Card variant="outlined">
                        <CardContent>
                            <FormLabel component="legend" required>Type of Incident</FormLabel>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.incidentTypeMedical}
                                            onChange={(e) => handleInputChange('incidentTypeMedical', e.target.checked)}
                                        />
                                    }
                                    label="Medical"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.incidentTypeSecurity}
                                            onChange={(e) => handleInputChange('incidentTypeSecurity', e.target.checked)}
                                        />
                                    }
                                    label="Security"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.incidentTypeAccident}
                                            onChange={(e) => handleInputChange('incidentTypeAccident', e.target.checked)}
                                        />
                                    }
                                    label="Accident"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.incidentTypeNearMiss}
                                            onChange={(e) => handleInputChange('incidentTypeNearMiss', e.target.checked)}
                                        />
                                    }
                                    label="Near Miss"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.incidentTypeOther}
                                            onChange={(e) => handleInputChange('incidentTypeOther', e.target.checked)}
                                        />
                                    }
                                    label="Other"
                                />
                            </FormGroup>
                            {report.incidentTypeOther && (
                                <TextField
                                    fullWidth
                                    label="Please specify other incident type"
                                    value={report.incidentTypeOtherDetails}
                                    onChange={(e) => handleInputChange('incidentTypeOtherDetails', e.target.value)}
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* Description */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Description
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description of Incident"
                                        value={report.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        multiline
                                        rows={4}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Immediate Actions Taken"
                                        value={report.immediateActions}
                                        onChange={(e) => handleInputChange('immediateActions', e.target.value)}
                                        multiline
                                        rows={3}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Parties Notified */}
                    <Card variant="outlined">
                        <CardContent>
                            <FormLabel component="legend">Parties Notified</FormLabel>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.notifiedDutyOfficer}
                                            onChange={(e) => handleInputChange('notifiedDutyOfficer', e.target.checked)}
                                        />
                                    }
                                    label="Duty Officer"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.notifiedSecurityFocalPoint}
                                            onChange={(e) => handleInputChange('notifiedSecurityFocalPoint', e.target.checked)}
                                        />
                                    }
                                    label="Security Focal Point"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.notifiedMd}
                                            onChange={(e) => handleInputChange('notifiedMd', e.target.checked)}
                                        />
                                    }
                                    label="MD"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.notifiedClient}
                                            onChange={(e) => handleInputChange('notifiedClient', e.target.checked)}
                                        />
                                    }
                                    label="Client"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={report.notifiedAigInsurer}
                                            onChange={(e) => handleInputChange('notifiedAigInsurer', e.target.checked)}
                                        />
                                    }
                                    label="AIG/Insurer"
                                />
                            </FormGroup>
                        </CardContent>
                    </Card>

                    {/* Follow-up */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Follow-up
                            </Typography>
                            <TextField
                                fullWidth
                                label="Follow-up Actions Required"
                                value={report.followupActions}
                                onChange={(e) => handleInputChange('followupActions', e.target.value)}
                                multiline
                                rows={3}
                            />
                        </CardContent>
                    </Card>

                    {/* Completion */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Completion Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Completed by"
                                        value={report.completedBy}
                                        onChange={(e) => handleInputChange('completedBy', e.target.value)}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Date"
                                        type="date"
                                        value={report.completedDate}
                                        onChange={(e) => handleInputChange('completedDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/')}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={() => handleSave(true)}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Submitting...' : 'Submit Report'}
                        </Button>
                    </Box>
                </Stack>
            </Paper>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default IncidentReportPage;
